import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"

// The minutes' Assign to pop-up (Kloepfer K3-36, 2026-10-19): an action's
// owner can be anyone at the meeting — Aaron, an outside attendee, and the
// ITD guests too — anyone on the project team, or an outside person by
// email. Same look as the Submittal Log's Assign to. A pick saves at once
// (PATCH …/items/:id/assign) and the owner chip updates in place.
const COLORS = { member: "#16a34a", outside: "#2563eb", guest: "#64748b" }

export default class extends Controller {
  static targets = ["modal", "subject", "tab", "search", "list", "emailBox", "email", "hint", "chosen", "people", "words"]

  connect() {
    this._people = { meeting: [], team: [], outside: [] }
    if (this.hasPeopleTarget) {
      try { this._people = JSON.parse(this.peopleTarget.textContent) } catch { /* empty lists */ }
    }
    this._onKeydown = (e) => {
      if (e.key === "Escape" && this.hasModalTarget && !this.modalTarget.hidden) this.close()
    }
    document.addEventListener("keydown", this._onKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
  }

  open(event) {
    if (!this.hasModalTarget) return
    const p = event.params
    this._item = {
      id: p.item,
      url: p.url,
      owner: p.name ? { userId: p.user ? String(p.user) : "", name: String(p.name), email: String(p.email || ""), kind: String(p.kind || "") } : null
    }
    this.subjectTarget.textContent = p.subject || ""
    this._tab = "meeting"
    this.searchTarget.value = ""
    this.emailTarget.value = ""
    this._paint()
    document.dispatchEvent(new CustomEvent("dropdown:close"))
    this.modalTarget.hidden = false
    this.searchTarget.focus()
  }

  close() {
    if (this.hasModalTarget) this.modalTarget.hidden = true
    this._item = null
  }

  stop(event) {
    event.stopPropagation()
  }

  tab(event) {
    this._tab = event.currentTarget.dataset.tab
    this.searchTarget.value = ""
    this._paint()
    this.searchTarget.focus()
  }

  search() {
    this._paintList()
  }

  pick(event) {
    const entry = this._entries().find((e) => e.key === event.currentTarget.dataset.key)
    if (!entry) return
    let body
    if (entry.attendee_id) body = { attendee_id: entry.attendee_id }
    else if (entry.user_id) body = { user_id: entry.user_id }
    else body = { email: entry.email, name: entry.name }
    this._save(body, { userId: entry.user_id ? String(entry.user_id) : "", name: entry.name, email: entry.email || "", kind: entry.kind }, true)
  }

  emailKeydown(event) {
    if (event.key !== "Enter") return
    event.preventDefault()
    this.addEmail()
  }

  addEmail() {
    const email = this.emailTarget.value.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      showToast(this._word("badEmail"), "error")
      this.emailTarget.focus()
      return
    }
    this._save({ email }, { userId: "", name: email, email, kind: "outside" }, true)
  }

  remove() {
    this._save({ clear: true }, null, false)
  }

  // ── private ───────────────────────────────────────────────────────────

  async _save(body, owner, closeAfter) {
    if (!this._item || this._busy) return
    this._busy = true
    try {
      const res = await fetch(this._item.url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify(body),
        credentials: "same-origin"
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) {
        showToast(data.error || this._word("failed"), "error")
        return
      }
      document.querySelectorAll(`[data-owner-chip="${data.item_id}"]`).forEach((el) => { el.outerHTML = data.owner_html })
      showToast(data.message)
      if (closeAfter) {
        this.close()
      } else {
        this._item.owner = owner
        this._paint()
      }
    } catch {
      showToast(this._word("failed"), "error")
    } finally {
      this._busy = false
    }
  }

  _paint() {
    this.tabTargets.forEach((t) => {
      const on = t.dataset.tab === this._tab
      t.classList.toggle("border-brand", on)
      t.classList.toggle("text-brand", on)
      t.classList.toggle("border-transparent", !on)
      t.classList.toggle("text-gray-500", !on)
    })
    this.emailBoxTarget.hidden = this._tab !== "outside"
    this.hintTarget.hidden = this._tab !== "meeting"
    this._paintList()
    this._paintChosen()
  }

  _entries() {
    return this._people[this._tab] || []
  }

  _paintList() {
    const fold = (t) => String(t ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
    const q = fold(this.searchTarget.value).trim()
    const all = this._entries()
    const people = all.filter((e) => !q || fold(`${e.name} ${e.email || ""}`).includes(q))
    if (!people.length) {
      this.listTarget.innerHTML = `<div class="px-4 py-8 text-[13px] text-gray-400 text-center">${this._esc(this._word(all.length ? "noMatch" : "noneYet"))}</div>`
      return
    }
    this.listTarget.innerHTML = people.map((e) => {
      const on = this._isOwner(e)
      return `<button type="button" data-key="${this._esc(e.key)}" data-action="click->minutes-assign#pick"
          class="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left cursor-pointer ${on ? "bg-brand/5" : "hover:bg-gray-50"}">
          <span class="w-4 h-4 shrink-0 rounded-full border ${on ? "border-brand" : "border-gray-300"} flex items-center justify-center">${on ? '<span class="w-2 h-2 rounded-full bg-brand"></span>' : ""}</span>
          ${this._avatar(e.name, COLORS[e.kind] || COLORS.guest, 32)}
          <span class="flex-1 min-w-0"><span class="block text-sm font-medium text-gray-900 truncate">${this._esc(e.name)}</span>
            <span class="block text-[13px] text-gray-500 truncate">${this._esc(this._secondLine(e))}</span></span>
        </button>`
    }).join("")
  }

  _paintChosen() {
    const o = this._item && this._item.owner
    if (!o) {
      this.chosenTarget.innerHTML = `<div class="px-3 py-8 text-[13px] text-gray-400 text-center">${this._esc(this._word("nobody"))}</div>`
      return
    }
    const tone = COLORS[o.kind] || COLORS.guest
    const line = o.kind === "member" ? (o.email || this._word("team"))
      : o.kind === "outside" ? `${o.email} · ${this._word("outside")} · ${this._word("getsLink")}`
        : this._word("nameOnly")
    this.chosenTarget.innerHTML = `<div class="flex items-center gap-3 px-2.5 py-2 rounded-lg" style="background:${tone}0F;border:1px solid ${tone}4D">
        ${this._avatar(o.name, tone, 32)}
        <span class="flex-1 min-w-0"><span class="block text-sm font-medium text-gray-900 truncate">${this._esc(o.name)}</span>
          <span class="block text-[13px] text-gray-500">${this._esc(line)}</span></span>
        <span class="shrink-0 rounded-full px-2 py-px text-[13px] font-semibold" style="background:${tone}24;color:${tone}">${this._esc(this._word("assigned"))}</span>
        <button type="button" data-action="click->minutes-assign#remove" title="${this._esc(this._word("remove"))}"
                class="shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer">✕</button>
      </div>`
  }

  _secondLine(e) {
    const bits = []
    if (e.kind === "guest") bits.push(this._word("noEmail"))
    else if (e.email) bits.push(e.email)
    if (e.kind === "outside" && this._tab !== "outside") bits.push(this._word("outside"))
    if (e.role) bits.push(e.role)
    return bits.join(" · ")
  }

  _isOwner(e) {
    const o = this._item && this._item.owner
    if (!o) return false
    if (o.userId && e.user_id) return String(e.user_id) === o.userId
    if (o.email && e.email) return e.email.toLowerCase() === o.email.toLowerCase()
    return !o.userId && !o.email && e.name === o.name
  }

  _avatar(name, color, size) {
    const words = String(name || "?").replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean)
    const letters = ((words[0] || "?")[0] + ((words[1] || "")[0] || "")).toUpperCase()
    return `<span class="shrink-0 rounded-full flex items-center justify-center text-[13px] font-semibold text-white" style="width:${size}px;height:${size}px;background:${color}">${this._esc(letters)}</span>`
  }

  _word(key) {
    const el = this.hasWordsTarget ? this.wordsTarget : null
    const attr = key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)
    return (el && el.getAttribute(`data-${attr}`)) || ""
  }

  _esc(text) {
    return String(text ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }
}
